import React from "react";
import { formatGuideDate } from "./blogFormat";

/**
 * GuideMeta - the ONE meta row shared by the /blog index hero, the guide
 * cards, and the article header. It renders, in order and hairline-separated:
 *
 *   <gold category>  |  <N min read>  |  Updated <date>
 *
 * Every segment is optional and guarded, so a post missing reading_time or a
 * date simply drops that segment (and its separator) without leaving a dangling
 * divider. Figures are tabular-nums so dates and read times align across the
 * grid. The "Updated" date is wrapped in a semantic <time> element carrying the
 * machine-readable ISO value.
 *
 * One system, one implementation: this is what makes the hero, the cards, and
 * the post header read as visibly the same family.
 */
const GuideMeta = ({ category, readingTime, updated, className = "" }) => {
  const segments = [];

  if (category) {
    segments.push(
      <span key="category" className="text-primary">
        {category}
      </span>,
    );
  }

  if (typeof readingTime === "number" && readingTime > 0) {
    segments.push(
      <span key="reading-time" className="tabular-nums text-muted-foreground">
        {readingTime} min read
      </span>,
    );
  }

  const formattedDate = formatGuideDate(updated);
  if (formattedDate) {
    segments.push(
      <span key="updated" className="tabular-nums text-muted-foreground">
        Updated{" "}
        <time dateTime={updated}>{formattedDate}</time>
      </span>,
    );
  }

  if (segments.length === 0) return null;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium ${className}`}
    >
      {segments.map((segment, index) => (
        <React.Fragment key={segment.key}>
          {index > 0 ? (
            <span aria-hidden="true" className="h-3 w-px bg-border" />
          ) : null}
          {segment}
        </React.Fragment>
      ))}
    </div>
  );
};

export default GuideMeta;
